import { useCallback, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SiteNav from '@/components/feature/SiteNav';
import type { RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CursorImageTrail } from '@/components/special-effects/cursor-emoji/components/base/CursorImageTrail';
import { ManifestoBody, trailItems } from './Manifesto';

gsap.registerPlugin(ScrollTrigger);

// Framed floral overflow hero: L0 white page → L1 navy stage → L2 HTML text → L3 alpha bouquet
const HERO_BOUQUET = 'https://public.readdy.ai/ai/img_res/sandbox/0a9d2030d78e6980171043cc5a230587/587ec097-3f6f-459e-9fda-bbab1d2fb4a2_ff-hero-bouquet.webp';

const HERO_DARK_BG = 'https://public.readdy.ai/ai/img_res/sandbox/0a9d2030d78e6980171043cc5a230587/587ec097-3f6f-459e-9fda-bbab1d2fb4a2_ff-hero-dark-bg.webp';
const HERO_SIDE_L = 'https://public.readdy.ai/ai/img_res/sandbox/0a9d2030d78e6980171043cc5a230587/587ec097-3f6f-459e-9fda-bbab1d2fb4a2_ff-hero-side-l.webp';
const HERO_SIDE_R = 'https://public.readdy.ai/ai/img_res/sandbox/0a9d2030d78e6980171043cc5a230587/587ec097-3f6f-459e-9fda-bbab1d2fb4a2_ff-hero-side-r.webp';

export default function Header() {
  const sectionRef = useRef<HTMLElement>(null);
  // Only spawn trail flowers over the dark stage, never over the white area
  const spawnInDark = useCallback((x: number, y: number) => {
    const block = sectionRef.current?.querySelector('.hero-block');
    if (!block) return false;
    const r = block.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }, []);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const ctx = gsap.context(() => {
      const q = gsap.utils.selector(section);
      const ease = 'power3.out';
      const mm = gsap.matchMedia();

      mm.add({ desktop: '(min-width: 768px)', mobile: '(max-width: 767px)' }, (c) => {
        const d = Boolean(c.conditions?.desktop);
        const k = d ? 1 : 0.6;

        // ---------- Entrance ----------
        gsap.set(q('.hero-block'), { clipPath: 'inset(100% 0% 0% 0%)', y: 30 * k });
        gsap.set(q('.hero-eyebrow, .hero-line, .hero-sub'), { opacity: 0, y: 20 * k });
        gsap.set(q('.hero-bouquet'), { opacity: 0, y: 30 * k, scale: 0.97, transformOrigin: '50% 100%' });

        const intro = gsap.timeline({ defaults: { ease } });
        intro
          .to(q('.hero-block'), { clipPath: 'inset(0% 0% 0% 0%)', y: 0, duration: d ? 0.85 : 0.6, ease: 'power3.inOut' })
          .to(q('.hero-eyebrow'), { opacity: 1, y: 0, duration: 0.6 }, d ? 0.45 : 0.3)
          .to(q('.hero-line'), { opacity: 1, y: 0, duration: 0.7, stagger: 0.12 }, d ? 0.55 : 0.38)
          .to(q('.hero-sub'), { opacity: 1, y: 0, duration: 0.6, stagger: 0.08 }, d ? 0.8 : 0.55)
          .to(q('.hero-bouquet'), { opacity: 1, y: 0, scale: 1, duration: d ? 1.0 : 0.7, ease: 'power2.out' }, d ? 0.6 : 0.4);

        // ---------- Scroll ----------
        if (d) {
          const block = q('.hero-block')[0] as HTMLElement;
          const wrap = q('.hero-bouquet-move')[0] as HTMLElement;
          const rel = (el: HTMLElement) => {
            const s = section.getBoundingClientRect();
            const r = el.getBoundingClientRect();
            return { x: r.left - s.left, y: r.top - s.top, w: r.width, h: r.height };
          };
          const blockInset = () => {
            const b = rel(block);
            const W = section.offsetWidth;
            const H = section.offsetHeight;
            return `inset(${b.y}px ${W - b.x - b.w}px ${H - b.y - b.h}px ${b.x}px)`;
          };
          gsap.set(q('.hero-side-l'), { xPercent: -100, autoAlpha: 0 });
          gsap.set(q('.hero-side-r'), { xPercent: 100, autoAlpha: 0 });
          gsap.set(q('.hero-manifesto'), { autoAlpha: 0, y: 40 });

          const tl = gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: {
              trigger: section,
              pin: section,
              start: 'top top',
              end: () => `+=${Math.round(window.innerHeight * 2)}`,
              scrub: 1,
              anticipatePin: 1,
              invalidateOnRefresh: true,
            },
          });
          // Phase 1 (0–0.18): text exits
          tl.to(q('.hero-text-move'), { y: -50, opacity: 0, duration: 0.18 }, 0)
            // Phase 2 (0.18–0.5): dark stage expands to fill the screen
            .set(q('.hero-cover'), { autoAlpha: 1, clipPath: blockInset }, 0.18)
            .fromTo(q('.hero-cover'), { clipPath: blockInset }, { clipPath: 'inset(0px 0px 0px 0px)', duration: 0.32, ease: 'power2.inOut', immediateRender: false }, 0.18)
            // Bouquet accelerates downward and is gone before the cover finishes
            .to(wrap, { y: () => window.innerHeight * 1.1, duration: 0.28, ease: 'power3.in' }, 0.1)
            .to(wrap, { opacity: 0, duration: 0.08 }, 0.3)
            // Side foliage peeks in from both edges
            .to(q('.hero-side-l'), { xPercent: -38, autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0.4)
            .to(q('.hero-side-r'), { xPercent: 38, autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0.44)
            // Manifesto rises into the full dark screen and holds
            .to(q('.hero-trail'), { autoAlpha: 0, duration: 0.06 }, 0.5)
            .to(q('.hero-manifesto'), { autoAlpha: 1, y: 0, duration: 0.2, ease: 'power2.out' }, 0.56)
            .to({}, { duration: 0.24 }, 0.76);
        } else {
          // Mobile: no pin, text fades, bouquet grows slightly
          gsap.timeline({
            defaults: { ease: 'none' },
            scrollTrigger: { trigger: q('.hero-stage')[0], start: 'top 30%', end: 'bottom top', scrub: 0.5 },
          })
            .to(q('.hero-text-move'), { y: -30, opacity: 0, duration: 0.4 }, 0)
            .to(q('.hero-bouquet-move'), { scale: 1.08, duration: 1 }, 0);
        }
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="top" className="relative bg-white md:h-screen md:flex md:flex-col">
      {/* Cursor flower trail over the first screen (listens on the whole hero, never blocks clicks) */}
      <CursorImageTrail
        items={trailItems}
        itemSize={110}
        trailLength={8}
        spawnDistance={80}
        rotationRange={18}
        containerRef={sectionRef as RefObject<HTMLElement>}
        canSpawn={spawnInDark}
        className="hero-trail !absolute inset-0 z-40 pointer-events-none"
      />
      {/* Scroll-transition layers (desktop only, hidden until the scroll sequence starts) */}
      <div aria-hidden="true" className="hero-cover absolute inset-0 z-10 bg-[#0d1216] bg-cover bg-center pointer-events-none invisible opacity-0" style={{ backgroundImage: `url(${HERO_DARK_BG})` }} />
      <img aria-hidden="true" alt="" src={HERO_SIDE_L} className="hero-side-l hidden md:block absolute left-0 top-1/2 -translate-y-1/2 h-[85vh] w-auto max-w-none z-20 mix-blend-screen pointer-events-none invisible" />
      <img aria-hidden="true" alt="" src={HERO_SIDE_R} className="hero-side-r hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-[85vh] w-auto max-w-none z-20 mix-blend-screen pointer-events-none invisible" />
      <div className="hero-manifesto hidden md:flex absolute inset-0 z-30 invisible">
        <ManifestoBody dark className="w-full h-full flex items-center justify-center px-10" />
      </div>
      <SiteNav />

      <div className="relative px-3 md:px-8 pt-1 md:pt-2">
        <h1 aria-label="Fleur & Form" className="w-full flex items-baseline justify-between font-light uppercase tracking-[-0.05em] leading-[0.82] whitespace-nowrap text-[12.5vw] md:text-[12vw]">
          <span className="-ml-[0.04em]">Fleur</span>
          <span className="ff-serif italic normal-case font-normal tracking-normal text-[0.55em]">&amp;</span>
          <span className="-mr-[0.03em]">Form</span>
        </h1>
      </div>

      <div className="relative flex flex-wrap items-baseline justify-between gap-2 px-4 md:px-10 py-3">
        <p className="ff-serif italic text-[14px] md:text-[15px]">Experimental floral design — Spring / Summer 26</p>
        <p className="hidden sm:block font-mono uppercase text-[10px] md:text-[11px] tracking-[0.12em] text-black/50">Studio 04 — Copenhagen</p>
      </div>

      <div className="relative md:flex-1 md:min-h-0 md:flex md:flex-col">
        {/* Stage wrapper: top padding is the white zone the flowers rise into. No overflow clipping. */}
        <div className="hero-stage relative w-full pt-[90px] sm:pt-[110px] md:pt-[90px] md:flex-1 md:min-h-0 md:flex md:flex-col">
          <div className="hero-stage-move relative z-0 w-full will-change-transform md:flex-1 md:flex md:flex-col">
            {/* L1 — dark stage */}
            <div className="hero-block relative w-full h-[460px] sm:h-[500px] md:h-auto md:flex-1 bg-[#0d1216]">
              <img aria-hidden="true" alt="" src={HERO_DARK_BG} className="absolute inset-0 w-full h-full object-cover object-right pointer-events-none" />
              {/* L2 — editorial text, left */}
              <div className="hero-text absolute left-[6%] md:left-[6%] top-8 sm:top-12 md:top-1/2 md:-translate-y-1/2 w-[60%] sm:w-[44%] md:w-[40%] text-[#f1ece2]">
                <div className="hero-text-move will-change-transform">
                  <p className="hero-eyebrow font-mono uppercase text-[10px] md:text-[11px] tracking-[0.2em] opacity-60">Floral Studio / 2026</p>
                  <h2 className="mt-4 md:mt-6 font-light tracking-[-0.03em] leading-[1] text-[32px] sm:text-[44px] md:text-[62px]">
                    <span className="hero-line block whitespace-nowrap">Let flowers</span>
                    <span className="hero-line block whitespace-nowrap ff-serif italic font-normal tracking-normal">take shape.</span>
                  </h2>
                  <p className="hero-sub hidden sm:block mt-4 md:mt-5 font-light text-[13px] md:text-[15px] leading-[1.6] opacity-75">
                    Seasonal compositions, made by hand.
                  </p>
                  <Link to="/shop" className="hero-sub relative z-30 inline-block mt-6 md:mt-9 text-[12px] md:text-[13px] tracking-wide border-b border-white/50 pb-0.5 whitespace-nowrap cursor-pointer hover:border-white transition-colors">
                    Explore the collection ↗
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* L3 — alpha bouquet; scroll wrapper covers stage area, img keeps original placement */}
          <div className="hero-bouquet-move absolute inset-0 z-20 pointer-events-none will-change-transform" style={{ transformOrigin: '70% 100%' }}>
            <img
              src={HERO_BOUQUET}
              width={676}
              height={1016}
              fetchPriority="high"
              alt="Sculptural arrangement of garden roses, peonies and dahlias in a dark stone vase — Fleur & Form"
              title="Fleur & Form sculptural floral arrangement"
              className="hero-bouquet absolute bottom-[24px] md:bottom-[22px] right-[-18%] sm:right-[6%] md:right-[calc(8%+90px)] h-[500px] sm:h-[600px] md:h-[min(783px,calc(100vh-110px))] w-auto max-w-none"
            />
          </div>
        </div>
      </div>
    </section>
  );
}